// Shared TinyMCE configuration for local/offline usage
export const createTinyMCEConfig = (options = {}) => {
  // Ensure options is a valid object
  if (!options || typeof options !== 'object') {
    options = {};
  }
  const baseConfig = {
    // Remove API key to avoid cloud validation
    // apiKey: '', // Removed to prevent API validation
    
    // Use GPL license to avoid external validation
    license_key: 'gpl',
    
    // Disable external validation and telemetry
    promotion: false,
    branding: false,
    
    // Disable external services and validation
    external_plugins: {},
    verify_html: false,
    
    // Disable automatic updates and validation
    auto_update: false,
    check_whitelist: false,
    
    // Tell TinyMCE to use imported skins and CSS
    skin: false,        // Use imported skin CSS
    content_css: false, // Use imported content CSS
    
    // Ensure proper initialization
    selector: 'textarea', // Default selector
    init_instance_callback: (editor) => {
      // Ensure editor is properly initialized
      try {
        if (editor && typeof editor === 'object') {
          // Mark editor as initialized
          editor.initialized = true;
          
          if (options.setup && typeof options.setup === 'function') {
            try {
              options.setup(editor);
            } catch (error) {
              console.warn('TinyMCE setup error:', error);
            }
          }
        }
      } catch (error) {
        console.warn('TinyMCE init_instance_callback error:', error);
      }
    },
    
    // Basic configuration
    height: (options.height && typeof options.height === 'number') ? options.height : 400,
    menubar: (options.menubar !== undefined) ? options.menubar : false,
    
    // Essential plugins only
    plugins: (options.plugins && Array.isArray(options.plugins)) ? options.plugins : [
      'autolink', 'link', 'lists', 'table', 'wordcount'
    ],
    
    // Simple toolbar
    toolbar: (options.toolbar && typeof options.toolbar === 'string') ? options.toolbar : 'undo redo | bold italic underline | link | numlist bullist | table | removeformat',
    
    // Content styling
    content_style: (options.content_style && typeof options.content_style === 'string') ? options.content_style : 'body { font-family: Helvetica, Arial, sans-serif; font-size: 14px }',
    
    // Placeholder text
    placeholder: (options.placeholder && typeof options.placeholder === 'string') ? options.placeholder : 'Enter content...',
    
    // Setup function for additional functionality
    setup: (editor) => {
      try {
        // Ensure editor is properly initialized before calling setup
        if (editor && typeof editor === 'object' && options.setup && typeof options.setup === 'function') {
          // Wait for next tick to ensure editor is fully ready
          setTimeout(() => {
            try {
              if (editor && typeof editor === 'object') {
                options.setup(editor);
              }
            } catch (error) {
              console.warn('TinyMCE setup error in delayed call:', error);
            }
          }, 0);
        }
      } catch (error) {
        console.warn('TinyMCE setup wrapper error:', error);
      }
    },
    
    // Image upload handler if provided
    ...(options.images_upload_handler && typeof options.images_upload_handler === 'function' && {
      images_upload_handler: options.images_upload_handler
    }),
    
    // Error handling
    onerror: (error) => {
      try {
        if (error && typeof error === 'object') {
          console.warn('TinyMCE error (non-critical):', error);
        }
      } catch (err) {
        console.warn('Error in TinyMCE error handler:', err);
      }
    },
    
    // Additional error handling for content operations
    onBeforeSetContent: (e) => {
      try {
        // Validate content before setting
        if (e && e.content && typeof e.content === 'string') {
          // Basic content validation
          if (e.content.length > 1000000) { // 1MB limit
            console.warn('Content too large, truncating');
            e.content = e.content.substring(0, 1000000);
          }
        }
      } catch (error) {
        console.warn('Content validation error:', error);
      }
    },
    
    // Additional safety measures
    forced_root_block: 'p',
    remove_script_host: false,
    convert_urls: false,
    relative_urls: false
  };

  try {
    return baseConfig;
  } catch (error) {
    console.warn('Error creating TinyMCE config:', error);
    // Return a minimal safe config
    return {
      height: 400,
      plugins: ['autolink', 'link', 'lists'],
      toolbar: 'undo redo | bold italic | link | numlist bullist',
      skin: false,
      content_css: false,
      license_key: 'gpl',
      promotion: false,
      branding: false
    };
  }
};

// Pre-configured configs for different use cases
export const blogEditorConfig = (options = {}) => createTinyMCEConfig({
  height: 500,
  plugins: [
    'anchor', 'autolink', 'charmap', 'codesample', 'emoticons', 'link', 'lists', 
    'media', 'searchreplace', 'table', 'visualblocks', 'wordcount', 'image', 'code'
  ],
  toolbar: 'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table | align lineheight | numlist bullist indent outdent | emoticons charmap codesample | code | removeformat',
  ...(options && typeof options === 'object' ? options : {})
});

export const courseEditorConfig = (options = {}) => createTinyMCEConfig({
  height: 400,
  plugins: ['autolink', 'link', 'lists', 'table', 'wordcount'],
  toolbar: 'undo redo | bold italic underline | link | numlist bullist | table | removeformat',
  placeholder: 'Enter course description...',
  ...(options && typeof options === 'object' ? options : {})
});
